/**
 * User Preferences Context
 *
 * Provides user preferences (timezone, etc.) throughout the app.
 * Reads from authenticated user data and provides update functionality.
 */

import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/context'
import { api } from '@/lib/api/client'
import { ENDPOINTS } from '@/lib/api/endpoints'
import type { UserPreferences } from '@/types'
import { getBrowserTimezone } from './timezones'

interface UserPreferencesContextType {
  /** User's saved preferences */
  preferences: UserPreferences
  /** Effective timezone (user preference or browser default) */
  effectiveTimezone: string
  /** Whether preferences are loading */
  isLoading: boolean
  /** Update user preferences */
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>
}

const UserPreferencesContext = createContext<UserPreferencesContextType | null>(null)

interface UserPreferencesProviderProps {
  children: ReactNode
}

export function UserPreferencesProvider({ children }: UserPreferencesProviderProps) {
  const { user, isLoading: authLoading, refreshUser } = useAuth()

  // Get preferences from user data
  const preferences = useMemo<UserPreferences>(() => {
    return user?.preferences || {}
  }, [user?.preferences])

  // Effective timezone: user preference or browser default
  const effectiveTimezone = useMemo(() => {
    return preferences.timezone || getBrowserTimezone()
  }, [preferences.timezone])

  // Update preferences via API
  const updatePreferences = useCallback(async (newPrefs: Partial<UserPreferences>) => {
    // Merge with existing preferences
    const mergedPreferences = {
      ...preferences,
      ...newPrefs,
    }

    // Call API to update
    await api.put(ENDPOINTS.auth.update, {
      preferences: mergedPreferences,
    })

    // Refresh user data to get updated preferences
    await refreshUser()
  }, [preferences, refreshUser])

  const value = useMemo<UserPreferencesContextType>(() => ({
    preferences,
    effectiveTimezone,
    isLoading: authLoading,
    updatePreferences,
  }), [preferences, effectiveTimezone, authLoading, updatePreferences])

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

/**
 * Hook to access user preferences
 */
export function useUserPreferences(): UserPreferencesContextType {
  const context = useContext(UserPreferencesContext)
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  return context
}
